import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';

import * as Core from '../../core';
import { HollowButton, SolidButton } from '../buttons-visuals';
import { ContentContainer } from '../containers';
import ConfirmModal from '../confirmModal';
import { Select, TextInput } from '../inputs';
import Modal from '../modal';
import { useLeague, useProfile, useUserPermissionService } from '../../hooks/store';
import { ClassSessionService } from '../../services/classSessionService';

import './index.scss';

interface ClassSessionScheduleProps {
    organizationId?: string;
    showCreate?: boolean;
}

interface SessionDayGroup {
    key: string;
    label: string;
    sortValue: number;
    sessions: Core.Models.ClassSession[];
}

const isUpcoming = (session: Core.Models.ClassSession) => session.currentState !== 'IsComplete';

const getLocalDayKey = (startTimeUtc?: string) => {
    if (!startTimeUtc) return 'tbd';
    const d = new Date(startTimeUtc);
    return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
};

const getLocalDayLabel = (startTimeUtc?: string) => {
    if (!startTimeUtc) return 'Time TBD';
    return new Date(startTimeUtc).toLocaleDateString(undefined, {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric',
    });
};

const groupSessionsByDate = (sessions: Core.Models.ClassSession[], ascending: boolean): SessionDayGroup[] => {
    const groups = new Map<string, SessionDayGroup>();

    for (const session of sessions) {
        const key = getLocalDayKey(session.startTimeUtc);
        const existing = groups.get(key);
        if (existing) {
            existing.sessions.push(session);
            continue;
        }

        groups.set(key, {
            key,
            label: getLocalDayLabel(session.startTimeUtc),
            sortValue: session.startTimeUtc ? new Date(session.startTimeUtc).setHours(0, 0, 0, 0) : Number.MAX_SAFE_INTEGER,
            sessions: [session],
        });
    }

    const ordered = [...groups.values()].sort((a, b) =>
        ascending ? a.sortValue - b.sortValue : b.sortValue - a.sortValue
    );

    for (const group of ordered) {
        group.sessions.sort((a, b) => {
            const aTime = a.startTimeUtc ? new Date(a.startTimeUtc).getTime() : 0;
            const bTime = b.startTimeUtc ? new Date(b.startTimeUtc).getTime() : 0;
            return ascending ? aTime - bTime : bTime - aTime;
        });
    }

    return ordered;
};

const ClassSessionSchedule = (props: ClassSessionScheduleProps): JSX.Element => {
    const league = useLeague();
    const profile = useProfile();
    const userPermissionService = useUserPermissionService();
    const organizationId = props.organizationId || profile?.organizationId;

    const [sessions, setSessions] = useState<Core.Models.ClassSession[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [pendingActionId, setPendingActionId] = useState<string | undefined>();
    const [sessionToDelete, setSessionToDelete] = useState<Core.Models.ClassSession | undefined>();

    const canManage = useMemo(() => {
        if (!organizationId) return false;
        return userPermissionService.hasOrganizationAccess(Core.Models.PermissionLevel.ElevatedEdit, organizationId);
    }, [organizationId, userPermissionService]);

    const load = useCallback(async () => {
        if (!organizationId || league?.featureGroup !== Core.Models.FeatureGroup.NCBC) return;
        setIsLoading(true);
        try {
            setSessions(await ClassSessionService.list(organizationId));
        } catch (e) {
            toast.error(Core.API.getErrorMessage(e));
        } finally {
            setIsLoading(false);
        }
    }, [league?.featureGroup, organizationId]);

    useEffect(() => {
        load();
    }, [load]);

    if (league?.featureGroup !== Core.Models.FeatureGroup.NCBC || !organizationId) return <></>;

    const upcoming = sessions.filter(isUpcoming);
    const past = sessions.filter((s) => !isUpcoming(s));
    const showCreateButton = (props.showCreate ?? true) && canManage;

    const confirmDelete = async () => {
        if (!sessionToDelete) return;
        const matchId = sessionToDelete.matchId;
        setPendingActionId(matchId);
        try {
            await ClassSessionService.delete(matchId);
            toast.success('Showcase deleted.');
            setSessionToDelete(undefined);
            await load();
        } catch (e) {
            toast.error(Core.API.getErrorMessage(e));
        } finally {
            setPendingActionId(undefined);
        }
    };

    const renderSessionCard = (session: Core.Models.ClassSession) => {
        const timeLabel = session.startTimeUtc
            ? new Date(session.startTimeUtc).toLocaleTimeString(undefined, {
                  hour: 'numeric',
                  minute: '2-digit',
              })
            : 'Time TBD';

        return (
            <li key={session.matchId}>
                <Link to={`/matches/${session.matchId}`} className="class-session-schedule__card">
                    <span className="class-session-schedule__card-surface">
                        {session.gameCardUrl && (
                            <img
                                className="class-session-schedule__card-image"
                                src={session.gameCardUrl}
                                alt=""
                                aria-hidden
                            />
                        )}
                        <span className="class-session-schedule__card-shade" />
                        {session.hasJoined && <span className="class-session-schedule__card-badge">Joined</span>}
                        {!session.hasJoined && isUpcoming(session) && (
                            <span className="class-session-schedule__card-badge class-session-schedule__card-badge--warn">
                                Not joined
                            </span>
                        )}
                        {!session.hasJoined && !isUpcoming(session) && (
                            <span className="class-session-schedule__card-badge">Completed</span>
                        )}
                        {session.canManage && isUpcoming(session) && (
                            <span className="class-session-schedule__card-delete">
                                <HollowButton
                                    as="button"
                                    size="small"
                                    color="destructive"
                                    disabled={!!pendingActionId}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setSessionToDelete(session);
                                    }}
                                >
                                    Delete
                                </HollowButton>
                            </span>
                        )}
                        <span className="class-session-schedule__card-body">
                            <span className="class-session-schedule__card-title">
                                {session.gameName}
                                {session.subject ? ` · ${session.subject}` : ''}
                            </span>
                            {session.topic && (
                                <span className="class-session-schedule__card-topic">{session.topic}</span>
                            )}
                            <span className="class-session-schedule__card-meta">
                                {timeLabel} · {session.participantCount} joined
                            </span>
                        </span>
                    </span>
                </Link>
            </li>
        );
    };

    const renderSection = (
        title: string,
        items: Core.Models.ClassSession[],
        ascending: boolean,
        emptyMessage?: string,
        controls?: React.ReactNode,
        emptyAction?: React.ReactNode,
        lede?: string
    ) => {
        const dayGroups = groupSessionsByDate(items, ascending);

        return (
            <ContentContainer
                className="class-session-schedule shadow-medium p0"
                shade={Core.Models.Shades.Dark20}
                radius={Core.Models.RadiusSizes.Large}
            >
                <div className="class-session-schedule__header">
                    <h2 className="heading-2 mb0" tabIndex={-1}>
                        {title}
                    </h2>
                    {controls && <div className="class-session-schedule__controls">{controls}</div>}
                </div>
                <div className="class-session-schedule__body">
                    {lede && <p className="class-session-schedule__lede">{lede}</p>}
                    {isLoading && <p className="class-session-schedule__empty">Loading showcases…</p>}
                    {!isLoading && items.length === 0 && emptyMessage && (
                        <div className="class-session-schedule__empty-state">
                            <p className="class-session-schedule__empty">{emptyMessage}</p>
                            {emptyAction}
                        </div>
                    )}
                    {!isLoading &&
                        dayGroups.map((day) => (
                            <section key={day.key} className="class-session-schedule__day">
                                <h3 className="class-session-schedule__day-heading">{day.label}</h3>
                                <ul className="class-session-schedule__grid">{day.sessions.map(renderSessionCard)}</ul>
                            </section>
                        ))}
                </div>
            </ContentContainer>
        );
    };

    const showcaseLede = showCreateButton
        ? 'Showcases are live class sessions you create and run at a scheduled time. Students join when one is posted — unlike Solo Play, which they can do anytime on their own.'
        : 'Showcases are live class sessions your professor creates and runs at a scheduled time. Join when one is posted — unlike Solo Play, which you can do anytime on your own.';

    const showcaseEmpty = showCreateButton
        ? 'No upcoming showcases yet. Create one for your class when you are ready to run a live session.'
        : 'No upcoming showcases for your school yet. Your professor will create and schedule them for class.';

    return (
        <div className="class-session-schedule-wrap" id="showcases">
            {renderSection(
                'Upcoming showcases',
                upcoming,
                true,
                showcaseEmpty,
                showCreateButton ? (
                    <SolidButton as="button" size="small" onClick={() => setShowCreateModal(true)}>
                        Create showcase
                    </SolidButton>
                ) : undefined,
                showCreateButton ? (
                    <SolidButton as="button" size="small" onClick={() => setShowCreateModal(true)}>
                        Create showcase
                    </SolidButton>
                ) : undefined,
                showcaseLede
            )}

            {past.length > 0 && renderSection('Recent showcases', past, false)}

            {showCreateModal && organizationId && (
                <CreateSessionModal
                    organizationId={organizationId}
                    onClose={() => setShowCreateModal(false)}
                    onCreated={async () => {
                        setShowCreateModal(false);
                        await load();
                    }}
                />
            )}

            {sessionToDelete && (
                <ConfirmModal
                    onCancel={() => setSessionToDelete(undefined)}
                    onConfirm={confirmDelete}
                    title="Delete showcase?"
                >
                    <p>
                        Delete{' '}
                        <strong>
                            {sessionToDelete.gameName}
                            {sessionToDelete.topic ? ` — ${sessionToDelete.topic}` : ''}
                        </strong>
                        ? This cannot be undone.
                    </p>
                </ConfirmModal>
            )}
        </div>
    );
};

const CreateSessionModal = ({
    organizationId,
    onClose,
    onCreated,
}: {
    organizationId: string;
    onClose: () => void;
    onCreated: () => Promise<void>;
}) => {
    const [games, setGames] = useState<Core.Models.QuizWhizzerCatalogGame[]>([]);
    const [topics, setTopics] = useState<Core.Models.QuizWhizzerTopic[]>([]);
    const [gameId, setGameId] = useState('');
    const [subject, setSubject] = useState('');
    const [topicId, setTopicId] = useState('');
    const [startLocal, setStartLocal] = useState('');
    const [pending, setPending] = useState(false);
    const [catalogLoading, setCatalogLoading] = useState(true);
    const [createdLaunchUrl, setCreatedLaunchUrl] = useState<string | undefined>();

    useEffect(() => {
        (async () => {
            setCatalogLoading(true);
            try {
                const list = await ClassSessionService.getCatalogGames();
                setGames(list);
                if (list.length === 0) {
                    toast.error(
                        'Showcase catalog is empty. A league host must open Settings → Showcase Catalog and click Initialize / seed catalog.'
                    );
                    return;
                }
                setGameId(list[0].id);
                setTopics(await ClassSessionService.getTopics(list[0].id));
            } catch (e) {
                toast.error(Core.API.getErrorMessage(e));
            } finally {
                setCatalogLoading(false);
            }
        })();
    }, []);

    const subjects = useMemo(
        () => [...new Set(topics.map((t) => t.subject))].sort((a, b) => a.localeCompare(b)),
        [topics]
    );

    const filteredTopics = useMemo(
        () => topics.filter((t) => !subject || t.subject === subject),
        [subject, topics]
    );

    const onGameChange = async (id: string) => {
        setGameId(id);
        setSubject('');
        setTopicId('');
        setTopics(await ClassSessionService.getTopics(id));
    };

    const minLocal = useMemo(() => {
        const d = new Date();
        d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
        return d.toISOString().slice(0, 16);
    }, []);

    const submit = async () => {
        if (!topicId || !startLocal) {
            toast.error('Select a topic and start time.');
            return;
        }
        const startUtc = new Date(startLocal);
        if (Number.isNaN(startUtc.getTime()) || startUtc.getTime() < Date.now() - 5 * 60 * 1000) {
            toast.error('Start time must be in the future.');
            return;
        }
        setPending(true);
        try {
            const created = await ClassSessionService.create({
                organizationId,
                topicId,
                startTimeUtc: startUtc.toISOString(),
            });
            toast.success('Showcase created.');
            if (created.launchUrl) {
                setCreatedLaunchUrl(created.launchUrl);
            } else {
                await onCreated();
            }
        } catch (e) {
            toast.error(Core.API.getErrorMessage(e));
        } finally {
            setPending(false);
        }
    };

    if (createdLaunchUrl) {
        return (
            <Modal onClose={() => void onCreated()} title="Showcase created">
                <p className="mb2x">
                    Open the launch link, start the game, then paste the access code on the showcase page.
                </p>
                <div className="disp-flex flex-gap flex-wrap">
                    <SolidButton
                        as="a"
                        href={createdLaunchUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        size="small"
                    >
                        Open showcase
                    </SolidButton>
                    <HollowButton as="button" onClick={() => void onCreated()} size="small">
                        Done
                    </HollowButton>
                </div>
            </Modal>
        );
    }

    return (
        <Modal onClose={onClose} title="Create showcase">
            {catalogLoading && <p>Loading catalog…</p>}
            {!catalogLoading && (
                <>
                    <Select
                        className="mb2x"
                        label="Game"
                        value={gameId}
                        onChange={(e) => void onGameChange(e.target.value)}
                    >
                        {games.map((g) => (
                            <option key={g.id} value={g.id}>
                                {g.name}
                            </option>
                        ))}
                    </Select>
                    <Select
                        className="mb2x"
                        label="Subject"
                        value={subject}
                        onChange={(e) => {
                            setSubject(e.target.value);
                            setTopicId('');
                        }}
                    >
                        <option value="">Select subject</option>
                        {subjects.map((s) => (
                            <option key={s} value={s}>
                                {s}
                            </option>
                        ))}
                    </Select>
                    <Select
                        className="mb2x"
                        label="Topic"
                        value={topicId}
                        onChange={(e) => setTopicId(e.target.value)}
                    >
                        <option value="">Select topic</option>
                        {filteredTopics.map((t) => (
                            <option key={t.id} value={t.id}>
                                {t.topic}
                            </option>
                        ))}
                    </Select>
                    <TextInput
                        className="mb2x"
                        label="Start time (your local time)"
                        type="datetime-local"
                        min={minLocal}
                        value={startLocal}
                        onChange={(e) => setStartLocal(e.target.value)}
                    />
                    <p className="mb2x text-small">
                        After creating, open the launch link, start the game, then paste the access code on the showcase
                        page.
                    </p>
                    <div className="disp-flex flex-gap">
                        <HollowButton as="button" onClick={onClose} size="small">
                            Cancel
                        </HollowButton>
                        <SolidButton as="button" onClick={submit} pending={pending} size="small">
                            Create
                        </SolidButton>
                    </div>
                </>
            )}
        </Modal>
    );
};

export default ClassSessionSchedule;
